import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Card, CardContent, Button, Box,
  CircularProgress, Alert, Grid, Chip
} from '@mui/material';
import { CheckCircle, Cancel, Business } from '@mui/icons-material';
import { getBusinessRequests, approveBusinessRequest, denyBusinessRequest } from '../services/api';

const AdminBusinessRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await getBusinessRequests();
      setRequests(data.requests || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (index) => {
    setProcessing(index);
    try {
      await approveBusinessRequest(index);
      await fetchRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeny = async (index) => {
    setProcessing(index);
    try {
      await denyBusinessRequest(index);
      await fetchRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deny request');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Business Sign-Up Requests
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {requests.length === 0 ? (
        <Alert severity="info">No pending business requests</Alert>
      ) : (
        <Grid container spacing={2}>
          {requests.map((request, index) => (
            <Grid item xs={12} key={index}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Business sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">
                        {request['Business Name']}
                      </Typography>
                      <Chip label={request['Business Type']} size="small" />
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Contact Person
                      </Typography>
                      <Typography variant="body2">
                        {request['Contact Person']}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2">
                        {request.Email}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body2">
                        {request.Phone}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Submission Date
                      </Typography>
                      <Typography variant="body2">
                        {request['Submission Date']}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Reason for Access
                      </Typography>
                      <Typography variant="body2">
                        {request['Reason for Access']}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => handleApprove(index)}
                      disabled={processing === index}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => handleDeny(index)}
                      disabled={processing === index}
                    >
                      Deny
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default AdminBusinessRequestsPage;
